import svgPath from 'svgpath';
import {
    createForeignObjectImageUrl,
    formatSvgPathDecimals,
    serializerHtmlToString,
    serializeSvgText,
} from '@/utils/file-kit';
import { type ILayerProps, type IVectorBorderRadiusProps, type IVectorLayerProps } from '@/version-types';
import { checkIsUsingBlur, checkIsUsingReflection, checkIsUsingShadow } from '../effect';
import { flatGraphColors } from '../parse';
import { SVGCreator } from './creator';

export const getSimpleRectSVGString = (
    width: number,
    height: number,
    config: {
        naturalWidth: number;
        naturalHeight: number;
        borderRadius: IVectorBorderRadiusProps;
        strokeWidth: number;
    },
) => {
    const { naturalWidth, naturalHeight, borderRadius, strokeWidth } = config;

    // 调整宽高以考虑内描边
    const _width = width - strokeWidth;
    const _height = height - strokeWidth;

    const offset = strokeWidth / 2;
    const { leftTop, rightTop, rightBottom, leftBottom } = borderRadius; // 四个角的半径

    const maxRadius = Math.ceil(Math.min(_width, _height) / 2);
    const tl = Math.min(leftTop, maxRadius);
    const tr = Math.min(rightTop, maxRadius);
    const br = Math.min(rightBottom, maxRadius);
    const bl = Math.min(leftBottom, maxRadius);

    // M ${tl + offset},${offset}                                       // M: 移动到左上角圆角的起始点
    // H ${_width - tr + offset}                                        // H: 画水平线到右上角圆角起始点
    // A ${tr},${tr} 0 0 1 ${_width + offset},${tr + offset}            // A: 绘制右上角圆角
    // V ${_height - br + offset}                                       // V: 画垂直线到右下角圆角起始点
    // A ${br},${br} 0 0 1 ${_width - br + offset},${_height + offset}  // A: 绘制右下角圆角
    // H ${bl + offset}                                                 // H: 画水平线到左下角圆角起始点
    // A ${bl},${bl} 0 0 1 ${offset},${_height - bl + offset}           // A: 绘制左下角圆角
    // V ${tl + offset}                                                 // V: 画垂直线到左上角圆角起始点
    // A ${tl},${tl} 0 0 1 ${tl + offset},${offset}                     // A: 绘制左上角圆角
    // Z                                                                // Z: 闭合路径

    const path = `
        M ${tl + offset},${offset}
        H ${_width - tr + offset}
        A ${tr},${tr} 0 0 1 ${_width + offset},${tr + offset}
        V ${_height - br + offset}
        A ${br},${br} 0 0 1 ${_width - br + offset},${_height + offset}
        H ${bl + offset}
        A ${bl},${bl} 0 0 1 ${offset},${_height - bl + offset}
        V ${tl + offset}
        A ${tl},${tl} 0 0 1 ${tl + offset},${offset}
        Z
    `;

    const pathD = formatSvgPathDecimals(
        svgPath(path)
            .scale(naturalWidth / width, naturalHeight / height)
            .toString(),
    );

    const svgWidth = Math.ceil(width);
    const svgHeight = Math.ceil(height);

    const content = `<svg
            width="${svgWidth}"
            height="${svgHeight}"
            viewBox="0 0 ${svgWidth} ${svgHeight}"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
        >
            <path d="${pathD}" fill="blue" />
        </svg>`;

    return content;
};

/**
 * 用于基于普通 svg 素材（非 graph）的图片信息
 * @param source - svg 字符串
 * @param layer - 矢量图层
 * @returns - 图片信息
 */
export const createNormalSVGImage = (source: string, layer: ILayerProps) => {
    const svgInfo = SVGCreator.create(source, layer);
    if (!svgInfo) return null;

    const { svgWidth, svgHeight, svgString } = svgInfo;
    const isUsingBlur = checkIsUsingBlur(layer.data as IVectorLayerProps);
    const isUsingReflection = checkIsUsingReflection(layer.data as IVectorLayerProps);
    const isUsingShadow = checkIsUsingShadow(layer.data as IVectorLayerProps);

    if (isUsingBlur || isUsingReflection || isUsingShadow) {
        return {
            width: svgWidth,
            height: svgHeight,
            src: svgString,
        };
    }

    const src = createForeignObjectImageUrl(
        svgWidth,
        svgHeight,
        serializerHtmlToString(serializeSvgText(svgString)),
        true,
    );
    return {
        width: svgWidth,
        height: svgHeight,
        src,
    };
};

/**
 * 用于创建 vectorType 为 graph 的图片信息
 * @param source - svg 字符串
 * @param layer - 矢量图层
 * @returns - 图片信息
 */
export const createGraphSVGImage = (source: string, layer: ILayerProps) => {
    const layerData = layer.data as IVectorLayerProps;
    const { colors, naturalWidth, naturalHeight } = layerData;

    let svgString = source;

    // 替换颜色
    const flatColors = flatGraphColors(colors);
    if (flatColors.length > 0) {
        flatColors.forEach((color, idx) => {
            svgString = svgString.replace(`{{colors[${idx}]}}`, `${color}`);
        });
    }

    const src = createForeignObjectImageUrl(
        naturalWidth,
        naturalHeight,
        serializerHtmlToString(serializeSvgText(svgString)),
        true,
    );

    return {
        width: naturalWidth,
        height: naturalHeight,
        src,
    };
};
